'use client';

import React, { useMemo, useRef } from 'react';
import type { Key, ListState } from 'react-stately';
import clsx from 'clsx';
import { getOffsetPaddingRight } from 'components/ScrollContainer/ScrollContainer';
import { Separator } from 'components/Separator';
import { useGetAdaptiveSpacingNumber } from 'theme-utils/utils/adaptiveThemingUtils';
import invariant from 'tiny-invariant';
import { AnimateItemGroup, useAnimateItem } from '../../animations';
import styles from '../../Collections.module.css';
import type { LayoutSection } from '../../layouts/types';
import type { ListVariants, SetSizeWithId } from '../types';
import { ListBoxItem, listBoxItemVariants } from './ListBoxItem';
import { ListHeader } from './ListHeader';

function isListVariant(value: unknown): value is ListVariants {
  return typeof value === 'string' && value in listBoxItemVariants;
}

type SectionProps<T> = {
  node: LayoutSection;
  state: ListState<T>;
  updateItemSize: SetSizeWithId;
  id: Key;
  y: number;
  sectionGap: number;
  isHeaderSticky?: boolean;
  isHeaderVisible?: boolean;
  className?: string;
};

function ListBoxSection<T>({
  y,
  node,
  state,
  updateItemSize,
  sectionGap,
  className,
}: SectionProps<T>) {
  const ref = useRef(null);
  const item = state.collection.getItem(node.key);
  const variant = item?.props?.variant;

  // Get variant's default marginX, allow per-section override
  const variantMarginXSpacing = isListVariant(variant)
    ? listBoxItemVariants[variant].marginX
    : 0;
  const variantMarginX = useGetAdaptiveSpacingNumber({
    spacing: variantMarginXSpacing,
  });
  const marginX = item?.props?.marginX ?? variantMarginX;
  // Only apply section marginX positioning if explicitly set (not from variant)
  const sectionX = item?.props?.marginX ?? 0;
  const { animated } = useAnimateItem(ref, { y, x: sectionX });
  const as = item?.props?.as || 'div';
  const hasDivider = item?.props?.divider;

  // Use the gap from the layout (which is the sectionGap from List)
  const effectiveGap = node.gapEnd ?? sectionGap;

  const marginLeft = marginX;
  const marginRight = getOffsetPaddingRight(marginX);

  const dividerStyle = useMemo(() => {
    if (!hasDivider) return {};
    return {
      top: y + node.rect.height + effectiveGap / 2,
    };
  }, [hasDivider, y, node.rect.height, effectiveGap]);

  const Component = as;

  return (
    <>
      <Component
        ref={ref}
        className={clsx(styles.section, className)}
        role="presentation"
        style={{
          height: node.rect.height,
          transition: animated ? 'height 0.3s ease' : undefined,
          width:
            sectionX > 0
              ? `calc(100% - ${sectionX + getOffsetPaddingRight(sectionX)}px)`
              : '100%',
        }}
      >
        {node.item.isVisible && (
          <ListHeader
            backgroundColor={item?.props?.headerBackground}
            fixedHeight={item?.props?.headerHeight}
            isPinned={node.isStickyHeaderPinned}
            isSticky={node.item.isSticky}
            nodeId={node.item.key}
            role="presentation"
            updateItemSize={updateItemSize}
            variant={variant}
          >
            {item?.rendered}
          </ListHeader>
        )}

        <div
          className={styles.sectionItems}
          role="presentation"
          style={{
            height: node.rect.height - node.item.rect.height,
          }}
        >
          <AnimateItemGroup>
            {node.children.map((childNode, i) => {
              const prevChild = node.children[i - 1];
              const item = state.collection.getItem(childNode.key);

              invariant(childNode.type === 'item', 'Expected item type');

              return (
                <ListBoxItem
                  key={childNode.key}
                  className={item?.props?.className}
                  gapAfter={childNode.gapEnd}
                  gapBefore={(prevChild as any)?.gapEnd}
                  height={childNode.rect.height}
                  id={childNode.key}
                  state={state}
                  updateItemSize={updateItemSize}
                  variant={variant}
                  y={childNode.rect.y - node.rect.y}
                >
                  {item?.rendered}
                </ListBoxItem>
              );
            })}
          </AnimateItemGroup>
        </div>
      </Component>

      {hasDivider && (
        <div
          className={styles.dividerContainer}
          style={{
            ...dividerStyle,
            paddingLeft: `${marginLeft}px`,
            paddingRight: `${marginRight}px`,
          }}
        >
          {typeof hasDivider === 'boolean' ? (
            <Separator color="primary" />
          ) : (
            hasDivider
          )}
        </div>
      )}
    </>
  );
}

const _ListBoxSection = React.memo(ListBoxSection);
_ListBoxSection.displayName = 'ListBoxSection';
export { _ListBoxSection as ListBoxSection };
